import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Briefcase,
  Loader,
  Check,
  X,
  Building2,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import Logo from '../../components/common/Logo'
import { authService } from "../../services/authService";
import { companyService } from "../../services/companyService";

const CompanySignup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    countryCode: "+91",
    phoneNumber: "",
    emailOtp: "",
    phoneOtp: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
    role: "company", // Fixed role for company
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpsId, setOtpsId] = useState(null);
  const [countryCodes, setCountryCodes] = useState([]);
  const [countryCodesLoading, setCountryCodesLoading] = useState(true);

  const { register } = useAuth();

  // Fetch country codes on component mount
  useEffect(() => {
    const fetchCountryCodes = async () => {
      try {
        setCountryCodesLoading(true);
        const response = await authService.getEnum('country_codes');
        if (response.success && response.data) {
          const transformedCodes = response.data.map(item => ({
            code: item.value,
            country: item.key.charAt(0) + item.key.slice(1).toLowerCase(),
            flag: getFlagEmoji(item.value)
          }));
          setCountryCodes(transformedCodes);
        } else {
          setCountryCodes([]);
        }
      } catch (error) {
        console.error('Failed to fetch country codes:', error);
        setCountryCodes([]);
      } finally {
        setCountryCodesLoading(false);
      }
    };

    fetchCountryCodes();
  }, []);

  // Helper function to get flag emoji from country code
  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return "🏳️";
    const code = countryCode.replace('+', '');
    const flagMap = {
      '91': '🇮🇳',   // India
      '1': '🇺🇸',    // USA
      '44': '🇬🇧',   // UK
      '61': '🇦🇺',   // Australia
      '971': '🇦🇪',  // UAE
      '49': '🇩🇪',   // Germany
      '33': '🇫🇷',   // France
      '81': '🇯🇵',   // Japan
      '86': '🇨🇳',   // China
      '55': '🇧🇷',   // Brazil
      '7': '🇷🇺',    // Russia
    };
    return flagMap[code] || "🏳️";
  };

  // Comprehensive form validation
  const validateForm = (validateOTP = false) => {
    const newErrors = {};

    if (!validateOTP) {
      // Company name validation
      if (!formData.companyName.trim()) {
        newErrors.companyName = "Company name is required";
      }

      // Email validation
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }

      // Phone number validation
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
      } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "Invalid phone number format";
      }

      // Password validation
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = "Password must contain uppercase and lowercase letters";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = "Password must contain at least one number";
      } else if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(formData.password)) {
        newErrors.password = "Password must contain at least one special character";
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }

      // Terms and conditions validation
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = "You must agree to the terms and conditions";
      }
    } else {
      // Validate OTP fields
      if (!formData.emailOtp.trim()) {
        newErrors.emailOtp = "Email OTP is required";
      } else if (!/^\d{6}$/.test(formData.emailOtp)) {
        newErrors.emailOtp = "Email OTP must be 6 digits";
      }

      if (!formData.phoneOtp.trim()) {
        newErrors.phoneOtp = "Phone OTP is required";
      } else if (!/^\d{6}$/.test(formData.phoneOtp)) {
        newErrors.phoneOtp = "Phone OTP must be 6 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPasswordStrength = (password) => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthColor = (strength) => {
    switch (strength) {
      case 1: return "bg-red-500";
      case 2: return "bg-orange-500";
      case 3: return "bg-yellow-500";
      case 4: return "bg-green-500";
      default: return "bg-gray-200 dark:bg-gray-700";
    }
  };

  const getPasswordStrengthText = (password) => {
    const strength = getPasswordStrength(password);
    switch (strength) {
      case 0: return "";
      case 1: return "Very weak";
      case 2: return "Weak";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "";
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If OTP not sent yet, send OTP
    if (!otpSent) {
      if (!validateForm(false)) return;

      setIsLoading(true);

      try {
        const response = await authService.sendOtp({
          role: "company",
          name: formData.companyName,
          email: formData.email,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (response.success) {
          setOtpsId(response.data.otps_id);
          setOtpSent(true);
          toast.success(response.message || "OTP sent successfully!");
        } else {
          toast.error(response.message || "Failed to send OTP.");
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      // OTP already sent, verify OTP and register
      if (!validateForm(true)) return;

      setIsLoading(true);

      try {
        // First verify email OTP
        const emailVerifyResponse = await authService.verifyEmailOtp({
          otps_id: otpsId,
          role: "company",
          name: formData.companyName,
          email: formData.email,
          email_otp: formData.emailOtp,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (!emailVerifyResponse.success) {
          toast.error(emailVerifyResponse.message || "Email OTP verification failed.");
          return;
        }

        // Then verify phone OTP
        const phoneVerifyResponse = await authService.verifyPhoneOtp({
          otps_id: otpsId,
          role: "company",
          name: formData.companyName,
          email: formData.email,
          phone: formData.phoneNumber,
          phone_otp: formData.phoneOtp,
          password: formData.password,
        });

        if (!phoneVerifyResponse.success) {
          toast.error(phoneVerifyResponse.message || "Phone OTP verification failed.");
          return;
        }

        // Finally register the user
        const registerResponse = await companyService.register({
          otps_id: otpsId,
          role: "company",
          name: formData.companyName,
          email: formData.email,
          phone: formData.phoneNumber,
          password: formData.password,
        });

        if (registerResponse.success || registerResponse.token) {
          const token = registerResponse.token || registerResponse.data?.token || registerResponse.data?.access_token
          const userId = registerResponse.data?.user_id || registerResponse.data?.id

          // 1. First save basic company details
          try {
            await companyService.saveCompanyDetails({
              company_name: formData.companyName,
              corporate_email: formData.email,
              contact_phone: formData.phoneNumber,
              // These are mandatory in API but we can send empty/defaults for now
              // or let them be updated in the onboarding flow
              legal_entity_name: formData.companyName,
              company_size: "",
              business_domain: "",
              website_url: "",
              pan_tin: "",
              gstin: "",
              contact_person_name: formData.companyName, // Or can be name
              contact_email: formData.email,
              address_line_1: "",
              city: "",
              state: "",
              country: "",
              postal_code: ""
            });
          } catch (companyError) {
            console.error("Failed to save initial company details:", companyError);
            // We continue anyway, as the user is created. They can fill this later.
            // Or we could show a warning toast.
          }

          const registrationCredentials = {
            email: formData.email,
            password: formData.password,
            token: token,
            role: "company"
          }

          // Force auto-login
          try {
            const loginResponse = await authService.login(registrationCredentials);
            if (loginResponse.success) {
              navigate("/company/setup", { replace: true });
              toast.success("Account created and logged in!");
            } else {
              navigate("/auth/login", {
                state: {
                  credentials: registrationCredentials,
                  message: "Account created! Please log in."
                }
              });
            }
          } catch (loginErr) {
            console.error("Auto-login failed", loginErr);
            navigate("/auth/login", {
              state: {
                message: "Account created! Please log in manually."
              }
            });
          }
        } else {
          toast.error(registerResponse.message || "Registration failed.");
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg"
      >
        <div className="card p-8 shadow-xl space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4"
            >
              <img
                src="/src/assets/Bench_logo.png"
                alt="AJA Bench Logo"
                className="w-12 h-12 object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/48?text=Logo' }}
              />
            </motion.div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              Create Company Account
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Start hiring top talent today
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Company Name */}
            <div className="space-y-1.5">
              <label className="label text-xs">
                Company Name
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange("companyName", e.target.value)}
                  disabled={otpSent}
                  className={`input pl-9 py-2 text-sm ${otpSent ? "bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed opacity-70" : ""
                    }`}
                  placeholder="Enter your company name"
                />
              </div>
              {errors.companyName && (
                <p className="text-error-500 text-xs mt-1">{errors.companyName}</p>
              )}
            </div>

            {/* Email & Phone - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="label text-xs">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    disabled={otpSent}
                    className={`input pl-9 py-2 text-sm ${otpSent ? "bg-slate-100 cursor-not-allowed opacity-70" : ""
                      }`}
                    placeholder="company@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-error-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="label text-xs">
                  Phone Number
                </label>
                <div className="flex gap-2">
                  <div className="w-16 relative">
                    <select
                      value={formData.countryCode}
                      onChange={(e) => handleInputChange("countryCode", e.target.value)}
                      disabled={otpSent || countryCodesLoading}
                      className={`input px-1 py-2 text-xs appearance-none cursor-pointer pr-4 ${otpSent || countryCodesLoading ? "bg-slate-100 cursor-not-allowed opacity-70" : ""
                        }`}
                    >
                      {countryCodesLoading ? (
                        <option>...</option>
                      ) : countryCodes.length === 0 ? (
                        <option disabled>No data</option>
                      ) : (
                        countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                      disabled={otpSent}
                      className={`input pl-9 py-2 text-sm ${otpSent ? "bg-slate-100 cursor-not-allowed opacity-70" : ""
                        }`}
                      placeholder="9876543210"
                    />
                  </div>
                </div>
                {errors.phoneNumber && (
                  <p className="text-error-500 text-xs mt-1">{errors.phoneNumber}</p>
                )}
              </div>
            </div>

            {/* Password & Confirm - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="label text-xs">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className="input pl-9 pr-9 py-2 text-sm"
                    placeholder="Create password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-1">
                    <div className="flex items-center space-x-1 mb-1">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-0.5 flex-1 rounded-full ${getPasswordStrength(formData.password) >= level
                            ? getPasswordStrengthColor(getPasswordStrength(formData.password))
                            : "bg-slate-200 dark:bg-slate-700"
                            }`}
                        />
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">
                      {getPasswordStrengthText(formData.password)}
                    </p>
                  </div>
                )}
                {errors.password && (
                  <p className="text-error-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="label text-xs">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className={`input pl-9 pr-9 py-2 text-sm ${formData.confirmPassword && formData.password !== formData.confirmPassword
                      ? "border-error-500"
                      : formData.confirmPassword && formData.password === formData.confirmPassword
                        ? "border-success-500"
                        : ""
                      }`}
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-error-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* OTP Fields */}
            {otpSent && (
              <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-4 duration-300">
                <div className="space-y-1.5">
                  <label className="label text-xs">
                    Email OTP
                  </label>
                  <input
                    type="text"
                    value={formData.emailOtp}
                    onChange={(e) => handleInputChange("emailOtp", e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength="6"
                    className="input text-center text-sm font-bold tracking-wider py-2"
                    placeholder="000000"
                  />
                  {errors.emailOtp && (
                    <p className="text-error-500 text-xs mt-1">{errors.emailOtp}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="label text-xs">
                    Phone OTP
                  </label>
                  <input
                    type="text"
                    value={formData.phoneOtp}
                    onChange={(e) => handleInputChange("phoneOtp", e.target.value.replace(/\D/g, "").slice(0, 6))}
                    maxLength="6"
                    className="input text-center text-sm font-bold tracking-wider py-2"
                    placeholder="000000"
                  />
                  {errors.phoneOtp && (
                    <p className="text-error-500 text-xs mt-1">{errors.phoneOtp}</p>
                  )}
                </div>
              </div>
            )}

            {/* Terms */}
            <div className="flex items-start py-2">
              <input
                type="checkbox"
                id="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={(e) => handleInputChange("agreeToTerms", e.target.checked)}
                className="w-3.5 h-3.5 mt-0.5 border-slate-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="agreeToTerms" className="ml-2 text-xs text-slate-600 dark:text-slate-400">
                I agree to the{" "}
                <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">Terms</a>
                {" "}and{" "}
                <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">Privacy Policy</a>
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-error-500 text-xs -mt-2">{errors.agreeToTerms}</p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full py-2.5 shadow-lg shadow-primary-500/25 group text-sm"
            >
              {isLoading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                  <span>{otpSent ? "Verifying..." : "Sending OTP..."}</span>
                </>
              ) : (
                <>
                  <span>{otpSent ? "Verify & Create Account" : "Send OTP"}</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="text-center pt-2">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              Already have an account?{" "}
              <Link to="/auth/login" className="text-primary-600 hover:text-primary-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompanySignup;
